import { Box, Typography, useMediaQuery, Tabs, Tab, Divider, Grid, TextField, useTheme, FormControl, InputLabel, MenuItem, Select, SelectChangeEvent, Button } from '@mui/material';
import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { ToolsDetails, Cause } from '../../../../../../components/custom';
import { CustomAlert } from '../../../../../../components/shared';
import { LayoutBase } from '../../../../../../layouts';
import { EmergencyRegistrationServices, IAccidents, IEmergencyRegistration } from '../../../../../../services/api/action-plan/EmergencyRegistration';
import { NavigateBefore, NavigateNext, Save } from '@mui/icons-material';
import { useAuthContext } from '../../../../../../context';

interface TabPanelProps {
    children?: React.ReactNode;
    index: number;
    value: number;
}

function CustomTabPanel(props: TabPanelProps) {
    const { children, value, index, ...other } = props;

    return (
        <div
            role="tabpanel"
            hidden={value !== index}
            id={`simple-tabpanel-${index}`}
            aria-labelledby={`simple-tab-${index}`}
            {...other}
        >
            {value === index && (
                <Box sx={{ p: 3 }}>
                    <Typography component='div'>{children}</Typography>
                </Box>
            )}
        </div>
    );
}

function tabProps(index: number) {
    return {
        id: `simple-tab-${index}`,
        'aria-controls': `simple-tabpanel-${index}`,
    };
}

export const EmergencySituationRegisterDetails = () => {

    const [alertInfo, setAlertInfo] = useState<{ open: boolean; message: string; severity: 'success' | 'error' | 'warning' | 'info' }>({
        open: false,
        message: '',
        severity: 'success'
    });

    const [value, setValue] = React.useState(0);
    const handleChangeTab = (event: React.SyntheticEvent, newValue: number) => {
        setValue(newValue);
    };
    const url = useParams();
    const theme = useTheme();
    const smDown = useMediaQuery(theme.breakpoints.down('sm'));
    const [valid, setValid] = useState<boolean>(false);
    const navigate = useNavigate();
    const location = useLocation();

    const { userData } = useAuthContext();
    const pagePermissionsString = useMemo(() => {
        return userData?.nivel_acesso?.PAE?.registro;
    }, [userData]);

    const hasActionPermission = useCallback((action: 'add' | 'edit' | 'remove' | 'view'): boolean => {
        if (typeof pagePermissionsString === 'string' && pagePermissionsString !== '') {
            const actions = pagePermissionsString.split(',').map(s => s.trim());
            return actions.includes(action);
        }
        return false;
    }, [pagePermissionsString]);

    const [data, setData] = useState<IEmergencyRegistration>({
        id_barragem: '',
        data_acionamento: '',
        nivel_acionado: '',
        acidente: '',
        descricao: '',
        causa: '',
        acoes: '',
        consequencias: '',
        data_encerramento: '',
        causas_acidente: {},
    });

    useEffect(() => {
        setData({
            id_barragem: '',
            data_acionamento: '',
            nivel_acionado: '',
            acidente: '',
            descricao: '',
            causa: '',
            acoes: '',
            consequencias: '',
            data_encerramento: '',
            causas_acidente: {},
        });
        fetchData();
    }, [location.pathname]);

    function verifyForm() {
        return !!data.data_acionamento && !!data.data_encerramento;
    }

    const handleNew = () => {
        if (!hasActionPermission('add')) {
            setAlertInfo({ open: true, message: 'Você não tem permissão para adicionar um novo registro.', severity: 'error' });
            return;
        }
        navigate(`/information_dam/${url.id}/emergency_situation_simulation/detalhes/novo`);
    };


    const fetchData = () => {
        if (url.idRegister) {
            EmergencyRegistrationServices.get(url.idRegister)
                .then((response) => {
                    if (response instanceof Error) {
                        console.log(response.message);
                    } else {
                        response.data_acionamento = response.data_acionamento ? new Date(response.data_acionamento).toISOString().substring(0, 10) : '';
                        response.data_encerramento = response.data_encerramento ? new Date(response.data_encerramento).toISOString().substring(0, 10) : '';
                        setData(response);
                    }
                });
        }
    };

    const handleParticipants = (value: IAccidents) => {
        setData(prevData => { if (prevData) { return { ...prevData, causas_acidente: value }; } return { causas_acidente: value } as IEmergencyRegistration; });
    };

    const handleChangeSelect = (event: SelectChangeEvent) => {
        setData(prevData => { if (prevData) { return { ...prevData, acidente: event.target.value }; } return { acidente: event.target.value } as IEmergencyRegistration; });
    };

    const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
        const { name, value } = e.target;
        const newData = {
            ...data,
            [name]: value,
        };
        setData(newData);
        setValid(false);
    };

    const handleSave = () => {
        const isEditing = url.idRegister !== 'novo';
        const hasAddPermission = hasActionPermission('add');
        const hasEditPermission = hasActionPermission('edit');

        if ((!isEditing && !hasAddPermission) || (isEditing && !hasEditPermission)) {
            setAlertInfo({ open: true, message: 'Você não tem permissão para realizar esta ação.', severity: 'error' });
            return;
        }
        setAlertInfo({ open: false, message: 'Registro excluido', severity: 'success' });
        if (url.idRegister == 'novo') {
            if (verifyForm()) {
                if (url.id) {
                    data.id_barragem = url.id;
                    EmergencyRegistrationServices.create(data)
                        .then((response) => {
                            if (response instanceof Error) {
                                setAlertInfo({ open: true, message: `Error: ${response.message}`, severity: 'error' });
                            } else {
                                setAlertInfo({ open: true, message: 'Registro Criado', severity: 'success' });
                                setTimeout(() => {
                                    navigate(`/information_dam/${url.id}/emergency_situation_register`);
                                }, 2000);
                            }
                        });
                }
            } else {
                setValid(true);
            }
        } else {
            if (url.idRegister !== undefined) {
                delete data.uuid;
                EmergencyRegistrationServices.upgrade(data, url.idRegister)
                    .then((response) => {
                        if (response instanceof Error) {
                            setAlertInfo({ open: true, message: `Error: ${response.message}`, severity: 'error' });
                        } else {
                            setAlertInfo({ open: true, message: 'Registro atualizado', severity: 'success' });
                            setTimeout(() => {
                                navigate(`/information_dam/${url.id}/emergency_situation_register`);
                            }, 2000);
                        }
                    });
            }
        }
    };

    const handleDelete = () => {
        if (!hasActionPermission('remove')) {
            setAlertInfo({ open: true, message: 'Você não tem permissão para deletar este registro.', severity: 'error' });
            return;
        }
        if (url.idRegister !== undefined && url.idRegister !== 'novo') {
            EmergencyRegistrationServices.delet(url.idRegister)
                .then((response) => {
                    if (response instanceof Error) {
                        console.log(response.message);
                        setAlertInfo({ open: true, message: `Error: ${response.message}`, severity: 'error' });

                    } else {
                        setAlertInfo({ open: true, message: 'Registro excluido', severity: 'success' });
                    }
                });
        }
    };

    const handleChangeButtonNext = () => {
        if (value < 1) {
            setValue(value + 1);
        }
    };
    const handleChangeButtonBefor = () => {
        if (value > 0) {
            setValue(value - 1);
        }
    };

    return (
        <LayoutBase
            title='Cadastro Situação Emergências'
            tollBar={<ToolsDetails
                showDeletButton={url.idRegister !== 'novo' && hasActionPermission('remove')}
                showSaveButton={url.idRegister == 'novo' ? hasActionPermission('add') : hasActionPermission('edit')}
                showNewButton={hasActionPermission('add')}
                onClickSave={() => handleSave()}
                onClickNew={() => handleNew()}
                onClickDelet={() => handleDelete()}
            />}
        >
            <Box padding={1}>
                <Box sx={{ borderBottom: 1, borderColor: 'divider' }} justifyContent={'space-around'} display={'flex'}>
                    <Tabs value={value} onChange={handleChangeTab} variant="scrollable" scrollButtons allowScrollButtonsMobile>
                        <Tab disabled sx={{ color: valid ? '#d95b40' : theme.palette.primary.main, '&.Mui-disabled': { color: valid ? '#d95b40' : theme.palette.primary.main } }} label="Informações Emergência" {...tabProps(0)} />
                        <Tab disabled sx={{ color: valid ? '#d95b40' : theme.palette.primary.main, '&.Mui-disabled': { color: valid ? '#d95b40' : theme.palette.primary.main } }} label="Causas/Consequências" {...tabProps(1)} />
                    </Tabs>
                </Box>
                <Box padding={1} display={'flex'} justifyContent={'space-between'} flexDirection={smDown ? 'column' : 'row'} gap={smDown ? '5px' : 0}>
                    <Button
                        variant='contained'
                        sx={{ borderRadius: '25px' }}
                        startIcon={<NavigateBefore />}
                        disabled={value == 0}
                        onClick={() => handleChangeButtonBefor()}
                    >Voltar</Button>
                    <Button
                        variant='contained'
                        sx={{ borderRadius: '25px' }}
                        startIcon={value != 1 ? <NavigateNext /> : <Save />}
                        onClick={value != 1 ? () => handleChangeButtonNext() : () => handleSave()}
                        disabled={
                            (url.idRegister == 'novo' && !hasActionPermission('add')) ||
                            (url.idRegister !== 'novo' && !hasActionPermission('edit'))
                        }
                    >{value != 1 ? 'Proximo' : 'Salvar'}</Button>

                </Box>
                <CustomTabPanel value={value} index={0}>
                    <Box>
                        <Box
                            sx={{ display: 'flex', flexDirection: 'row', justifyContent: 'start' }}
                            padding={1}
                        >
                            <Typography><strong>Informações gerais de emergência</strong></Typography>
                        </Box>
                        <Divider />
                        <Grid container padding={2} spacing={2}>
                            <Grid item xs={smDown ? 12 : 6}>
                                <TextField
                                    label='Data acionamento'
                                    type='date'
                                    name='data_acionamento'
                                    fullWidth
                                    InputLabelProps={{ shrink: true }}
                                    onChange={(e) => handleChange(e)}
                                    value={data?.data_acionamento}
                                    error={valid ? (data.data_acionamento == '' ? true : false) : false}
                                    required
                                />
                            </Grid>
                            <Grid item xs={smDown ? 12 : 6}>
                                <TextField
                                    label='Data encerramento'
                                    type='date'
                                    name='data_encerramento'
                                    fullWidth
                                    InputLabelProps={{ shrink: true }}
                                    onChange={(e) => handleChange(e)}
                                    value={data?.data_encerramento}
                                    error={valid ? (data.data_encerramento == '' ? true : false) : false}
                                    required
                                />
                            </Grid>
                            <Grid item xs={smDown ? 12 : 6}>
                                <TextField
                                    label='Nível acionado'
                                    type='text'
                                    name='nivel_acionado'
                                    fullWidth
                                    InputLabelProps={{ shrink: true }}
                                    onChange={(e) => handleChange(e)}
                                    value={data?.nivel_acionado}
                                />
                            </Grid>
                            <Grid item xs={smDown ? 12 : 6}>
                                <FormControl fullWidth>
                                    <InputLabel id='acidente'>Acidente ou Incidente</InputLabel>
                                    <Select
                                        labelId='acidente'
                                        id="acidente"
                                        value={data.acidente}
                                        label="Acidente ou Incidente"
                                        onChange={handleChangeSelect}
                                    >
                                        <MenuItem value={'Acidente'}>Acidente</MenuItem>
                                        <MenuItem value={'Incidente'}>Incidente</MenuItem>
                                    </Select>
                                </FormControl>
                            </Grid>
                            <Grid item xs={smDown ? 12 : 6}>
                                <TextField
                                    label='Breve descrição do evento '
                                    type='text'
                                    name='descricao'
                                    fullWidth
                                    InputLabelProps={{ shrink: true }}
                                    onChange={(e) => handleChange(e)}
                                    value={data?.descricao}
                                />
                            </Grid>
                            <Grid item xs={smDown ? 12 : 6}>
                                <TextField
                                    label='Causa da emergência'
                                    type='text'
                                    name='causa'
                                    fullWidth
                                    InputLabelProps={{ shrink: true }}
                                    onChange={(e) => handleChange(e)}
                                    value={data?.causa}
                                />
                            </Grid>
                            <Grid item xs={smDown ? 12 : 6}>
                                <TextField
                                    label='Ações realizadas após o evento '
                                    type='text'
                                    name='acoes'
                                    fullWidth
                                    InputLabelProps={{ shrink: true }}
                                    onChange={(e) => handleChange(e)}
                                    value={data?.acoes}
                                />
                            </Grid>
                            <Grid item xs={smDown ? 12 : 6}>
                                <TextField
                                    label='Consequências do evento  '
                                    type='text'
                                    name='consequencias'
                                    fullWidth
                                    InputLabelProps={{ shrink: true }}
                                    onChange={(e) => handleChange(e)}
                                    value={data?.consequencias}
                                />
                            </Grid>
                        </Grid>
                    </Box>
                </CustomTabPanel>

                <CustomTabPanel value={value} index={1}>
                    <Cause value={data.causas_acidente} onChange={handleParticipants} smDown={smDown} />
                </CustomTabPanel>

            </Box>
            {alertInfo.open &&
                <CustomAlert
                    severity={alertInfo.severity}
                    message={alertInfo.message}
                />
            }
        </LayoutBase>
    );
};